import { getInquiryAcceptPeriodMonths } from 'constants/setting';
import moment from 'moment';
import { stringify } from 'qs';
import { InitialStateType, StatusBooking, THandleChangeInfoCustomer } from '../context';
import { ICustomerInformation, ICustomerInformation2 } from '../pages/EnterCustomerInformation/types';
import { IBookingOnlineNewCustomer } from '../pages/StepConfirmationOfReservationDetail/types';
import {
  IAvailableReservationBooking, ICalenderBooking, IDateCalender,
} from '../pages/StepDateAndTimeVisitSelection/types';
import { ISelectOption, TOptionList } from '../pages/StepsBeforeReservation/types';

export enum KeyGetId {
  code = 'code',
  orgId = 'orgId',
  contractCourseCategoryId = 'contractCourseCategoryId',
  contractCourseMstId = 'contractCourseMstId',
  areaId = 'areaId',
}

export enum KeyGetText {
  value = 'value',
  courseCategoryName = 'courseCategoryName',
  courseName = 'courseName',
  areaName = 'areaName',
  orgName = 'orgName',
}
const formatSelectOptionList = (list: any, keyGetId: keyof typeof KeyGetId, keyGetText: keyof typeof KeyGetText):ISelectOption[] => list?.map((item:any) => ({ id: String(item[`${keyGetId}`]), text: item[`${keyGetText}`] }));

const getIconByStatus = (status:string) => {
  switch (status) {
    case StatusBooking.AVAILABLE_BOOKING:
      return '◎';
    case StatusBooking.CALL_BEFORE_BOOKING:
      return '△';
    default:
      return ''; // StatusBooking.CAN_NOT_BOOKING
  }
};

const getClassByStatus = (status:string):number => {
  switch (status) {
    case StatusBooking.AVAILABLE_BOOKING:
      return 0;
    case StatusBooking.CALL_BEFORE_BOOKING:
      return 1;
    default:
      return 2; // StatusBooking.CAN_NOT_BOOKING
  }
};

const getClassByDate = (type:number):number => {
  switch (type) {
    case 0: // 0 is sunday 'tableCalendar_day_sunday'
      return 0;
    case 6: // 6 is saturday 'tableCalendar_day_saturday'
      return 6;
    default:
      return type;
  }
};

const formatDataAvailableReservation = (data:IAvailableReservationBooking[]):[ICalenderBooking[][], IDateCalender[]] => {
  const arrTimeCalender:any = [];
  const arrDateCalender = data?.map((item, index) => {
    const { targetDate, workingCalendars } = item;
    workingCalendars.forEach((element, i) => {
      arrTimeCalender[i] === undefined && (arrTimeCalender[i] = []);
      const timeArr = String(element?.time).split(':');
      arrTimeCalender[i][index] = {
        key: `${targetDate}_${element?.time}`,
        time: element?.time,
        timeShort: timeArr[1] !== '00' ? timeArr[1] : element?.time,
        date: targetDate,
        status: element.status,
        icon: getIconByStatus(element.status),
        class: getClassByStatus(element.status),
      };
    });
    return ({
      dayOfWeek: moment(targetDate).format('M/DD (ddd)'),
      date: targetDate,
      class: getClassByDate(moment(targetDate).day()),
    });
  });
  return [arrTimeCalender, arrDateCalender];
};


const getDateByReservationAcceptanceDeadline = (key:string) => {
  switch (key) {
    case 'JUST_BEFORE':
      return moment().format('YYYY年MM月DD日(dd)');
    case 'BEFORE_THREE_HOURS':
      return moment().format('YYYY年MM月DD日(dd)');
    case 'NEXT_DAY':
      return moment().add(1, 'days').format('YYYY年MM月DD日(dd)');
    case 'TWO_DAYS_AFTER':
      return moment().add(2, 'days').format('YYYY年MM月DD日(dd)');
    default:
      return '';
  }
};

const getInquiryLimitDate = (inquiryAcceptPeriodMonthCode?: string | null): moment.Moment | null => {
  const months = getInquiryAcceptPeriodMonths(inquiryAcceptPeriodMonthCode);
  return months === null ? null : moment().startOf('day').add(months, 'months');
};

const getInquiryDeadlineTime = (inquiryAcceptanceDeadline?: string | null): moment.Moment | null => {
  switch (inquiryAcceptanceDeadline) {
    case 'JUST_BEFORE':
      return moment();
    case 'BEFORE_THREE_HOURS':
      return moment().add(3, 'hours');
    case 'NEXT_DAY':
      return moment().add(1, 'days').startOf('day');
    case 'TWO_DAYS_AFTER':
      return moment().add(2, 'days').startOf('day');
    default:
      return null;
  }
};

const filterTimesByBookingGap = (times: string[], openTime: string, bookingGapMinutes?: number | null): string[] => {
  const gap = Number(bookingGapMinutes);
  if (!gap || gap <= 0) return times;
  const openHour = Number(String(openTime).split(':')[0]);
  if (Number.isNaN(openHour)) return times;
  const anchorMinutes = openHour * 60;
  return times.filter((item) => {
    const [hour, minute] = item.split(':').map(Number);
    if (Number.isNaN(hour) || Number.isNaN(minute)) return false;
    return ((((hour * 60 + minute) - anchorMinutes) % gap) + gap) % gap === 0;
  });
};

const getDesiredYear = (month: string | number): number => {
  const monthNumber = Number(month);
  const now = moment();
  if (!monthNumber) return now.year();
  return monthNumber < now.month() + 1 ? now.year() + 1 : now.year();
};

const getDesiredDateString = (month: string, day: string): string => (
  (month && day) ? `${getDesiredYear(month)}-${month}-${day}` : ''
);

const getInquiryMonthOptions = (inquiryAcceptPeriodMonthCode?: string | null, inquiryAcceptanceDeadline?: string | null): string[] => {
  const limitDate = getInquiryLimitDate(inquiryAcceptPeriodMonthCode);
  const deadlineTime = getInquiryDeadlineTime(inquiryAcceptanceDeadline);
  const startMonth = deadlineTime ? deadlineTime.clone().startOf('month') : moment().startOf('month');
  if (!limitDate) {
    const allMonths = Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0'));
    return startMonth.isAfter(moment(), 'month') ? allMonths.filter((item) => item !== moment().format('MM')) : allMonths;
  }
  const monthOptions: string[] = [];
  const cursor = startMonth.clone();
  while (cursor.isSameOrBefore(limitDate, 'month')) {
    monthOptions.push(cursor.format('MM'));
    cursor.add(1, 'month');
  }
  return monthOptions;
};

const getInquiryDayOptions = (month: string, inquiryAcceptPeriodMonthCode?: string | null, inquiryAcceptanceDeadline?: string | null): string[] => {
  if (!month) return [];
  const limitDate = getInquiryLimitDate(inquiryAcceptPeriodMonthCode);
  const today = moment().startOf('day');
  const deadlineTime = getInquiryDeadlineTime(inquiryAcceptanceDeadline);
  const deadlineDay = deadlineTime ? deadlineTime.clone().startOf('day') : null;
  const earliestDay = (deadlineDay && deadlineDay.isAfter(today, 'day')) ? deadlineDay : today;
  const targetMonth = moment(`${getDesiredYear(month)}-${month}-01`, 'YYYY-MM-DD');
  if (!targetMonth.isValid()) return [];

  let from = targetMonth.clone().startOf('month');
  let to = targetMonth.clone().endOf('month');
  if (from.isBefore(earliestDay, 'day')) {
    from = earliestDay.clone();
  }
  if (limitDate && to.isAfter(limitDate, 'day')) {
    to = limitDate.clone();
  }
  if (from.isAfter(to, 'day')) return [];

  const dayOptions: string[] = [];
  const cursor = from.clone();
  while (cursor.isSameOrBefore(to, 'day')) {
    dayOptions.push(cursor.format('DD'));
    cursor.add(1, 'day');
  }
  return dayOptions;
};

const formatDataInfoCustomer = (data: ICustomerInformation2):THandleChangeInfoCustomer => ({
  customerFirstName: data?.customerFirstName || '',
  customerLastName: data?.customerLastName || '',
  customerFirstNameNameInFurigawa: data?.customerFirstNameNameInFurigawa || '',
  customerLastNameNameInFurigawa: data?.customerLastNameNameInFurigawa || '',
  customerBirthdayYear: data?.customerBirthdayYear || '',
  customerBirthdayMonth: data?.customerBirthdayMonth || '',
  customerBirthdayDay: data?.customerBirthdayDay || '',
  phoneNumber1: data?.phoneNumber1 || '',
  phoneNumber2: data?.phoneNumber2 || '',
  phoneNumber3: data?.phoneNumber3 || '',
  emailAddress: data?.emailAddress || '',
  consultationOrRequest: data?.consultationOrRequest || '',
  isEmailNewsLetter: data?.isEmailNewsLetter || '',
  desiredMonth1: data?.desiredMonth1 || '',
  desiredDay1: data?.desiredDay1 || '',
  desiredHour1: data?.desiredHour1 || '',
  desiredMonth2: data?.desiredMonth2 || '',
  desiredDay2: data?.desiredDay2 || '',
  desiredHour2: data?.desiredHour2 || '',
  desiredMonth3: data?.desiredMonth3 || '',
  desiredDay3: data?.desiredDay3 || '',
  desiredHour3: data?.desiredHour3 || '',
});

const formatDataInfoCustomerToSubmit = (context: InitialStateType):IBookingOnlineNewCustomer => {
  const {
    chosenCourse, chosenStore, chosenDesiredService, desiredTime, customerFirstName, customerLastName,
    customerFirstNameNameInFurigawa, customerLastNameNameInFurigawa,
    customerBirthdayYear, customerBirthdayMonth, customerBirthdayDay, phoneNumber1, phoneNumber2, phoneNumber3,
    emailAddress, consultationOrRequest, isEmailNewsLetter,
    desiredMonth2, desiredDay2, desiredHour2, desiredMonth3, desiredDay3, desiredHour3, status, companyCode,
  } = context;
  return ({
    companyCode: companyCode || '',
    birthdate: `${customerBirthdayYear}-${customerBirthdayMonth}-${customerBirthdayDay}`,
    contractCourseId: chosenCourse || '',
    customerFirstName: customerFirstName || '',
    customerFirstNameKana: customerFirstNameNameInFurigawa || '',
    customerLastName: customerLastName || '',
    customerLastNameKana: customerLastNameNameInFurigawa || '',
    desiredTime: `${moment(desiredTime, 'YYYYMM月DD日 HH:mm').format('YYYY-MM-DD HH:mm:00')}`,
    email: emailAddress || '',
    genderCode: { code: chosenDesiredService === '1' ? '0' : '1', value: chosenDesiredService === '1' ? '女性' : '男性' }, //
    orgId: chosenStore || '',
    phoneNumber: `${phoneNumber1}${phoneNumber2}${phoneNumber3}`,
    prefectureCode: { code: '', value: '' }, //
    requests: consultationOrRequest || '',
    sendableDm: isEmailNewsLetter === 'true',
    status: status || '',
    reserveFrameId: '',
    secondDesiredTime: (desiredMonth2 && desiredDay2) ? `${getDesiredDateString(desiredMonth2, desiredDay2)} ${desiredHour2}:00` : '',
    thirdDesiredTime: (desiredMonth3 && desiredDay3) ? `${getDesiredDateString(desiredMonth3, desiredDay3)} ${desiredHour3}:00` : '',
  });
};

const returnHistoryPath = (companyCode:string, currentStep:number = 0, courseCode:string = '', mediaCode:string = '', gender:string = '', serviceCode:string = '', trackingCode:string = ''):string => {
  const params: Record<string, string | number> = { companyCode };
  if (courseCode) params.courseCode = courseCode;
  if (mediaCode) params.mediaCode = mediaCode;
  if (gender) params.gender = gender;
  if (serviceCode) params.serviceCode = serviceCode;
  if (trackingCode) params.trackingCode = trackingCode;
  if (currentStep) params.currentStep = currentStep;
  return stringify(params);
};

export {
  formatSelectOptionList, formatDataAvailableReservation, getDateByReservationAcceptanceDeadline, formatDataInfoCustomer, formatDataInfoCustomerToSubmit, returnHistoryPath,
  getInquiryLimitDate, getInquiryDeadlineTime, getDesiredYear, getDesiredDateString, getInquiryMonthOptions, getInquiryDayOptions, filterTimesByBookingGap,
};
